import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { CyDatepicker, CyTextInput, CyTooltip, CyButton } from '@vwfs-uix/core/dist/react';
import { IconName } from '@vwfs-uix/core/dist/components/icon/icon.enum';
import { CyEvent } from '@vwfs-uix/core/dist/utils/events';
import { useTranslation } from 'react-i18next';

export const legacyToIso = (legacy: string): string => {
    if (!legacy) return '';
    const parts = legacy.match(/(\d+)/g);
    if (!parts || parts.length < 3) return '';
    return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
};

export const isoToLegacy = (iso: string): string => {
    if (!iso) return '';
    const dateOnly = iso.split('T')[0];
    const [y, m, d] = dateOnly.split('-');
    if (!y || !m || !d) return '';
    return `${d}.${m}.${y}`;
};

export const isoToFrench = (iso: string): string => {
    if (!iso) return '';
    const dateOnly = iso.split('T')[0];
    const [y, m, d] = dateOnly.split('-');
    if (!y || !m || !d) return '';
    return `${d}/${m}/${y}`;
};

export const toDateOnly = (iso?: string): string => (iso ? iso.split('T')[0] : '');

type LocaleConfig = {
    options: Intl.DateTimeFormatOptions;
    pattern: RegExp;
};

export const localesConfig: Record<string, LocaleConfig> = {
    de: {
        options: { year: 'numeric', month: '2-digit', day: '2-digit' },
        pattern: /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/,
    },
    en: {
        options: { year: 'numeric', month: '2-digit', day: '2-digit' },
        pattern: /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/,
    },
    fr: {
        options: { year: 'numeric', month: '2-digit', day: '2-digit' },
        pattern: /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/,
    },
};

export type CharlyDatepickerProps = {
    value?: string;
    onChange: (iso: string) => void;
    min?: string;
    max?: string;
    label?: string;
    required?: boolean;
    disabled?: boolean;
    className?: string;
    errorMessage?: string;
    name?: string;
    displayFormat?: 'dd/mm/yyyy' | 'dd/mm/yy';
};

const FR_COMPLETE = /^(\d{2})\/(\d{2})\/(\d{4})$/;
const FR_COMPLETE_SHORT = /^(\d{2})\/(\d{2})\/(\d{2})$/;

const applyDateMask = (input: string, yearDigits: 2 | 4): string => {
    const digits = input.replace(/\D/g, '').slice(0, yearDigits === 2 ? 6 : 8);
    const day = digits.slice(0, 2);
    const month = digits.slice(2, 4);
    const year = digits.slice(4, 4 + yearDigits);

    const dayWithPlaceholder = day.padEnd(2, ' ');
    const monthWithPlaceholder = month.padEnd(2, ' ');
    const yearWithPlaceholder = year.padEnd(yearDigits, ' ');
    return `${dayWithPlaceholder}/${monthWithPlaceholder}/${yearWithPlaceholder}`;
};

const expandTwoDigitYear = (year: string): string => {
    const now = new Date();
    const currentYY = Number(now.getFullYear().toString().slice(-2));
    const yy = Number(year);
    const century = yy <= currentYY ? 2000 : 1900;
    return `${century + yy}`;
};

const isoToFrenchShort = (iso: string): string => {
    if (!iso) return '';
    const dateOnly = iso.split('T')[0];
    const [y, m, d] = dateOnly.split('-');
    if (!y || !m || !d) return '';
    return `${d}/${m}/${y.slice(-2)}`;
};

export const CharlyDatepicker: FunctionComponent<CharlyDatepickerProps> = ({
    value = '',
    onChange,
    min,
    max,
    label,
    required,
    disabled,
    className,
    errorMessage,
    name,
    displayFormat = 'dd/mm/yyyy',
}) => {
    const ref = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const lastInputRef = useRef<string>('');
    const [internalError, setInternalError] = useState<string | undefined>(undefined);
    const { t } = useTranslation('translation');
    const isShortYearFormat = displayFormat === 'dd/mm/yy';

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const stop = (e: Event) => e.stopPropagation();
        const types = ['click', 'keydown', 'keyup', 'keypress', 'input'];
        types.forEach((type) => el.addEventListener(type, stop));
        return () => types.forEach((type) => el.removeEventListener(type, stop));
    }, []);

    const onChangeHandler = (event: CyEvent): void => {
        const iso = event.detail;
        if (typeof iso !== 'string' || !iso) return;
        const dateOnly = toDateOnly(iso);
        ref.current?.setAttribute(
            'defaultValue',
            isShortYearFormat ? isoToFrenchShort(dateOnly) : isoToFrench(dateOnly),
        );
        setInternalError(undefined);
        onChange(dateOnly);
    };

    const onInputHandler = (event: CyEvent): void => {
        const text = event.detail;
        if (typeof text !== 'string') return;
        const masked = applyDateMask(text, isShortYearFormat ? 2 : 4);
        lastInputRef.current = masked;
        ref.current?.setAttribute('defaultValue', masked);

        const match = masked.match(isShortYearFormat ? FR_COMPLETE_SHORT : FR_COMPLETE);
        if (!match) return;

        const [, day, month, year] = match;
        const normalizedYear = isShortYearFormat ? expandTwoDigitYear(year) : year;
        const iso = `${normalizedYear}-${month}-${day}`;
        if (isNaN(new Date(iso).getTime())) return;
        onChange(iso);
    };

    const onBlurHandler = (): void => {
        ref.current?.shadowRoot?.querySelector('input')?.classList.add('is-touched');
        const text = lastInputRef.current;
        const isComplete = isShortYearFormat ? FR_COMPLETE_SHORT.test(text) : FR_COMPLETE.test(text);
        if (text && !isComplete) {
            setInternalError(
                isShortYearFormat
                    ? 'Veuillez saisir une date valide (jj/mm/aa)'
                    : 'Veuillez saisir une date valide (jj/mm/aaaa)',
            );
        } else {
            setInternalError(undefined);
        }
    };

    return (
        <div ref={containerRef}>
            <CyTextInput
                // @ts-expect-error ref type is compatible enough
                ref={ref}
                type="text"
                id={name || undefined}
                label={label}
                placeholder={isShortYearFormat ? 'jj/mm/aa' : 'jj/mm/aaaa'}
                defaultValue={
                    value
                        ? isShortYearFormat
                            ? isoToFrenchShort(value)
                            : isoToFrench(value)
                        : isShortYearFormat
                        ? '  /  /  '
                        : ''
                }
                required={required || false}
                requiredText={required ? '*' : undefined}
                disabled={disabled}
                errorMessage={internalError || errorMessage}
                pattern={
                    isShortYearFormat ? '^(\\d{1,2})\\/(\\d{1,2})\\/(\\d{2})$' : '^(\\d{1,2})\\/(\\d{1,2})\\/(\\d{4})$'
                }
                onCyInput={onInputHandler}
                onCyBlur={onBlurHandler}
                className={className}
            >
                <CyTooltip placementInline="center" placementBlock="end" slot="suffix">
                    <CyButton
                        slot="tooltip-trigger"
                        appearance="ghost"
                        iconOnly
                        icon={IconName.CALENDAR}
                        disabled={disabled}
                        type="button"
                        aria-label={t('showCalendar')}
                    >
                        {t('showCalendar')}
                    </CyButton>
                    <CyDatepicker
                        slot="tooltip"
                        value={toDateOnly(value)}
                        locale="fr"
                        min={min}
                        max={max}
                        onCyChange={onChangeHandler}
                        yearSelect
                    />
                </CyTooltip>
            </CyTextInput>
        </div>
    );
};
